import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Check, Send, ChevronDown, ChevronUp, Zap, Target, Users, TrendingUp, Phone, Clock, DollarSign, Award, MessageSquare, BarChart3, Briefcase, Star, Rocket, FileText, Shield, Calendar, Settings, Utensils, ImageIcon, Pen, Bot, AlertTriangle, CheckCircle, XCircle, HelpCircle, Video, BookOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Access codes (case insensitive - stored in lowercase for comparison)
const ACCESS_CODES = {
  setter: 'setter5',
  closer: 'closer10'
};

// ============ THEME ============
const theme = {
  bg: '#f5f3f0',
  card: '#ffffff',
  card2: '#f0ede8',
  border: '#e0dbd4',
  text: '#1a1a1a',
  textMuted: 'rgba(26,26,26,0.58)',
  textLight: 'rgba(26,26,26,0.42)',
  orange: '#F05A1A',
  orangeDim: 'rgba(240,90,26,0.08)',
  orangeBorder: 'rgba(240,90,26,0.22)',
  green: '#16a34a',
  greenBg: 'rgba(22,163,74,0.08)',
  red: '#dc2626',
  redBg: 'rgba(220,38,38,0.08)',
  yellow: '#d97706',
  yellowBg: 'rgba(245,158,11,0.07)',
};

// ============ REUSABLE COMPONENTS ============

// Section Header
const SectionHeader = ({ num, title, highlight }) => (
  <div className="mb-6">
    <div className="font-bebas text-xs tracking-[3px] mb-1" style={{ color: theme.orange }}>
      {num}
    </div>
    <h2 className="font-bebas text-4xl leading-none" style={{ color: theme.text }}>
      {title} <span style={{ color: theme.orange }}>{highlight}</span>
    </h2>
  </div>
);

// Pill Badge
const Pill = ({ children }) => (
  <motion.span
    whileHover={{ scale: 1.05, y: -2 }}
    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold tracking-wide uppercase cursor-pointer"
    style={{ 
      backgroundColor: theme.orangeDim, 
      border: `1px solid ${theme.orangeBorder}`,
      color: theme.orange 
    }}
  >
    {children}
  </motion.span>
);

// Info Box (highlighted box)
const InfoBox = ({ title, children, icon }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="rounded-2xl p-6 my-5"
    style={{ 
      background: `linear-gradient(135deg, ${theme.orangeDim}, rgba(240,90,26,0.02))`,
      border: `1px solid ${theme.orangeBorder}`
    }}
  >
    {title && (
      <div className="flex items-center gap-2 mb-3">
        {icon && <span className="text-lg">{icon}</span>}
        <h4 className="font-bebas text-lg tracking-wider" style={{ color: theme.orange }}>{title}</h4>
      </div>
    )}
    <div className="text-sm leading-relaxed" style={{ color: theme.textMuted }}>
      {children}
    </div>
  </motion.div>
);

// Alert Box
const AlertBox = ({ type = 'info', children }) => {
  const styles = {
    info: { bg: theme.yellowBg, border: 'rgba(245,158,11,0.28)', icon: '💡', color: theme.yellow },
    success: { bg: theme.greenBg, border: 'rgba(22,163,74,0.25)', icon: '✓', color: theme.green },
    warning: { bg: theme.redBg, border: 'rgba(220,38,38,0.15)', icon: '!', color: theme.red }
  };
  const s = styles[type];
  
  return (
    <div 
      className="flex gap-3 items-start rounded-xl p-4 my-4"
      style={{ backgroundColor: s.bg, border: `1px solid ${s.border}` }}
    >
      <span className="text-lg pt-0.5">{s.icon}</span>
      <div className="text-sm leading-relaxed" style={{ color: theme.textMuted }}>
        {children}
      </div>
    </div>
  );
};

// Profile Card
const ProfileCard = ({ initials, name, tags, bio }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="flex gap-5 items-start rounded-2xl p-6 my-5"
    style={{ 
      backgroundColor: theme.card, 
      border: `1px solid ${theme.border}`,
      boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
    }}
  >
    <div 
      className="w-16 h-16 min-w-[64px] rounded-full flex items-center justify-center font-bebas text-2xl text-white"
      style={{ background: `linear-gradient(135deg, ${theme.orange}, #ff9a5c)` }}
    >
      {initials}
    </div>
    <div>
      <div className="font-bold text-lg mb-2" style={{ color: theme.text }}>{name}</div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {tags.map((tag, i) => (
          <span 
            key={i}
            className="text-[9px] font-bold tracking-wide uppercase px-2 py-1 rounded-full"
            style={{ 
              color: theme.orange, 
              backgroundColor: theme.orangeDim,
              border: `1px solid ${theme.orangeBorder}`
            }}
          >
            {tag}
          </span>
        ))}
      </div>
      <div className="text-sm leading-relaxed" style={{ color: theme.textMuted }}>
        {bio}
      </div>
    </div>
  </motion.div>
);

// Tips Grid Card
const TipCard = ({ icon, title, text }) => (
  <motion.div
    whileHover={{ borderColor: theme.orangeBorder, y: -3 }}
    className="rounded-xl p-5 transition-all cursor-pointer"
    style={{ 
      backgroundColor: theme.card, 
      border: `1px solid ${theme.border}`,
      boxShadow: '0 1px 6px rgba(0,0,0,0.03)'
    }}
  >
    <div className="text-2xl mb-3">{icon}</div>
    <div className="text-xs font-extrabold uppercase tracking-wide mb-2" style={{ color: theme.text }}>{title}</div>
    <div className="text-xs leading-relaxed" style={{ color: theme.textLight }}>{text}</div>
  </motion.div>
);

// Step Item
const StepItem = ({ num, title, text }) => (
  <div className="flex gap-4 py-4 border-b" style={{ borderColor: theme.border }}>
    <div className="font-bebas text-3xl min-w-[30px] pt-1" style={{ color: theme.orange }}>{num}</div>
    <div>
      <div className="text-sm font-extrabold mb-1" style={{ color: theme.text }}>{title}</div>
      <div className="text-sm leading-relaxed" style={{ color: theme.textLight }}>{text}</div>
    </div>
  </div>
);

// Timeline Item
const TimelineItem = ({ icon, title, desc, badge, badgeColor = 'orange', isLast = false }) => {
  const badgeStyles = {
    orange: { bg: theme.orangeDim, color: theme.orange, border: theme.orangeBorder },
    green: { bg: theme.greenBg, color: theme.green, border: 'rgba(22,163,74,0.2)' },
    grey: { bg: 'rgba(26,26,26,0.04)', color: theme.textMuted, border: theme.border }
  };
  const bs = badgeStyles[badgeColor];
  
  return (
    <div className="flex gap-0 items-stretch">
      <div className="flex flex-col items-center w-11 min-w-[44px]">
        <div 
          className="w-9 h-9 rounded-full flex items-center justify-center text-lg z-10"
          style={{ backgroundColor: theme.orangeDim, border: `2px solid ${theme.orange}` }}
        >
          {icon}
        </div>
        {!isLast && <div className="w-0.5 flex-1 my-1" style={{ backgroundColor: theme.border }} />}
      </div>
      <div className="flex-1 pb-6 pl-3.5">
        <div className="text-base font-extrabold pt-1.5 mb-1" style={{ color: theme.text }}>{title}</div>
        <div className="text-sm leading-relaxed mb-2" style={{ color: theme.textLight }}>{desc}</div>
        {badge && (
          <span 
            className="inline-block text-[10px] font-bold tracking-wide uppercase px-3 py-1 rounded-full"
            style={{ backgroundColor: bs.bg, color: bs.color, border: `1px solid ${bs.border}` }}
          >
            {badge}
          </span>
        )}
      </div>
    </div>
  );
};

// Trame Block (Call Script)
const TrameBlock = ({ time, phase, emoji, text, quote }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    className="rounded-r-xl p-5 mb-3"
    style={{ 
      backgroundColor: theme.card, 
      borderLeft: `3px solid ${theme.orange}`,
      boxShadow: '0 1px 6px rgba(0,0,0,0.03)'
    }}
  >
    <div className="text-[10px] font-bold tracking-wider uppercase mb-1" style={{ color: theme.orange }}>{time}</div>
    <div className="text-sm font-extrabold mb-2" style={{ color: theme.text }}>{emoji} {phase}</div>
    <div className="text-sm leading-relaxed mb-3" style={{ color: theme.textMuted }}>{text}</div>
    {quote && (
      <div 
        className="p-3 rounded-lg text-sm italic leading-relaxed"
        style={{ 
          backgroundColor: 'rgba(240,90,26,0.05)',
          borderLeft: `2px solid ${theme.orangeBorder}`,
          color: theme.textMuted
        }}
      >
        {quote}
      </div>
    )}
  </motion.div>
);

// Module Card (Expandable)
const ModuleCard = ({ num, icon, name, weeks, description, isOpen, onClick }) => (
  <motion.div
    layout
    onClick={onClick}
    className="rounded-xl overflow-hidden cursor-pointer transition-all"
    style={{ 
      backgroundColor: theme.card, 
      border: isOpen ? `2px solid ${theme.orangeBorder}` : `1px solid ${theme.border}`,
      boxShadow: isOpen ? '0 4px 16px rgba(0,0,0,0.08)' : '0 1px 5px rgba(0,0,0,0.03)'
    }}
    whileHover={{ scale: 1.01 }}
  >
    <div className="p-4 flex items-center gap-4">
      <span className="font-bebas text-xl" style={{ color: theme.orange }}>{num}</span>
      <span className="text-xl">{icon}</span>
      <div className="flex-1">
        <div className="text-sm font-extrabold" style={{ color: theme.text }}>{name}</div>
        <div className="text-xs" style={{ color: theme.orange }}>{weeks}</div>
      </div>
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        className="text-sm"
        style={{ color: theme.orange }}
      >
        ↓
      </motion.div>
    </div>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="px-4 pb-4"
        >
          <div className="pt-3 border-t text-sm leading-relaxed" style={{ borderColor: theme.border, color: theme.textMuted }}>
            {description}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

// Objection Card (FAQ style)
const ObjectionCard = ({ question, answer, isOpen, onClick }) => (
  <motion.div
    layout
    onClick={onClick}
    className="rounded-xl overflow-hidden cursor-pointer transition-all"
    style={{ 
      backgroundColor: theme.card, 
      border: `1px solid ${isOpen ? theme.orangeBorder : theme.border}`,
      boxShadow: '0 1px 5px rgba(0,0,0,0.03)'
    }}
    whileHover={{ borderColor: theme.orangeBorder }}
  >
    <div className="p-4 flex items-center justify-between gap-3">
      <span className="text-sm font-bold" style={{ color: 'rgba(26,26,26,0.65)' }}>{question}</span>
      <span className="text-sm" style={{ color: theme.orange }}>{isOpen ? '↑' : '↓'}</span>
    </div>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="px-4 pb-4"
        >
          <div 
            className="p-4 rounded-lg text-sm leading-relaxed"
            style={{ backgroundColor: 'rgba(240,90,26,0.03)', borderTop: `1px solid ${theme.border}` }}
            dangerouslySetInnerHTML={{ __html: answer }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

// Checklist Row
const CheckRow = ({ type = 'yes', children }) => (
  <div 
    className="flex items-start gap-3 p-3 rounded-lg"
    style={{ 
      backgroundColor: type === 'yes' ? theme.greenBg : theme.redBg,
      border: `1px solid ${type === 'yes' ? 'rgba(22,163,74,0.2)' : 'rgba(220,38,38,0.15)'}`
    }}
  >
    <span className="text-sm font-extrabold pt-0.5" style={{ color: type === 'yes' ? theme.green : theme.red }}>
      {type === 'yes' ? '✓' : '✗'}
    </span>
    <span className="text-sm font-semibold" style={{ color: 'rgba(26,26,26,0.65)' }}>{children}</span>
  </div>
);

// Pricing Card
const PricingCard = ({ featured, label, price, subtitle, commission, commissionSub }) => (
  <motion.div
    whileHover={{ y: -8, scale: 1.02 }}
    className="rounded-2xl p-6 cursor-pointer transition-all"
    style={{ 
      backgroundColor: featured 
        ? `linear-gradient(135deg, rgba(240,90,26,0.05), rgba(240,90,26,0.02))`
        : theme.card,
      background: featured 
        ? `linear-gradient(135deg, rgba(240,90,26,0.05), rgba(240,90,26,0.02))`
        : theme.card,
      border: featured ? `2px solid ${theme.orange}` : `2px solid ${theme.border}`,
      boxShadow: '0 1px 8px rgba(0,0,0,0.03)'
    }}
  >
    {featured && (
      <span 
        className="inline-block text-[10px] font-extrabold tracking-wider uppercase px-3 py-1 rounded-full mb-4"
        style={{ backgroundColor: theme.orange, color: '#fff' }}
      >
        MEILLEURE OFFRE
      </span>
    )}
    <div className="text-xs mb-1.5" style={{ color: theme.textMuted }}>{label}</div>
    <div className="font-bebas text-5xl leading-none" style={{ color: theme.text }}>
      {price.includes('×') ? (
        <>
          {price.split('×')[0]}× <span style={{ color: theme.orange }}>{price.split('×')[1]}</span>
        </>
      ) : (
        <span style={{ color: theme.orange }}>{price}</span>
      )}
    </div>
    <div className="text-xs mt-1 mb-5" style={{ color: theme.textLight }}>{subtitle}</div>
    
    <div className="pt-4 border-t" style={{ borderColor: theme.border }}>
      <div className="text-[10px] font-bold tracking-wider uppercase mb-1" style={{ color: theme.textMuted }}>TA COMMISSION</div>
      <div className="font-bebas text-3xl" style={{ color: theme.green }}>{commission}</div>
      <div className="text-xs" style={{ color: theme.textLight }}>{commissionSub}</div>
    </div>
  </motion.div>
);

// Remu Card
const RemuCard = ({ label, amount, subtitle, highlighted = false }) => (
  <div 
    className="rounded-xl p-5"
    style={{ 
      backgroundColor: highlighted 
        ? `linear-gradient(135deg, rgba(240,90,26,0.07), rgba(240,90,26,0.02))`
        : theme.card,
      background: highlighted 
        ? `linear-gradient(135deg, rgba(240,90,26,0.07), rgba(240,90,26,0.02))`
        : theme.card,
      border: `1px solid ${highlighted ? theme.orangeBorder : theme.border}`,
      boxShadow: '0 1px 6px rgba(0,0,0,0.03)'
    }}
  >
    <div className="text-[10px] font-bold tracking-wider uppercase mb-2" style={{ color: theme.textMuted }}>{label}</div>
    <div className="font-bebas text-4xl leading-none mb-1" style={{ color: theme.orange }}>{amount}</div>
    <div className="text-xs leading-relaxed" style={{ color: theme.textLight }}>{subtitle}</div>
  </div>
);

// Results Grid Card
const ResultCard = ({ icon, value, label }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="rounded-2xl p-5 text-center"
    style={{ 
      backgroundColor: theme.card, 
      border: `1px solid ${theme.border}`,
      boxShadow: '0 1px 6px rgba(0,0,0,0.03)'
    }}
  >
    <div className="text-3xl mb-3">{icon}</div>
    <div className="font-bebas text-4xl leading-none" style={{ color: theme.orange }}>{value}</div>
    <div className="text-xs font-semibold mt-1" style={{ color: theme.textMuted }}>{label}</div>
  </motion.div>
);

// Application Form
const ApplicationForm = ({ role }) => {
  const [formData, setFormData] = useState({
    firstName: '', instagram: '', email: '', phone: '', availability: '',
    experience: '', restaurantKnowledge: '', diagnosticApproach: '', objectionResponse: '', whyYou: '', tools: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await fetch(`${API_URL}/api/recruitment/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, role })
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi. Vérifie tes informations.');
      }
      
      setIsSubmitted(true);
    } catch (error) {
      console.error(error);
      setSubmitError(error.message || 'Une erreur est survenue. Réessaie.');
    }
    setIsSubmitting(false);
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12 rounded-2xl"
        style={{ backgroundColor: theme.card, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
      >
        <div className="font-bebas text-5xl mb-4" style={{ color: theme.orange }}>Candidature reçue.</div>
        <p className="text-sm" style={{ color: theme.textMuted }}>
          DM Instagram dans les 48h.<br />
          En attendant — étudie le diagnostic audit-resto.fr.<br />
          C'est le dossier que tu recevras avant chaque appel.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: theme.textLight }}>Prénom *</label>
          <input
            type="text"
            required
            value={formData.firstName}
            onChange={(e) => setFormData({...formData, firstName: e.target.value})}
            placeholder="Ton prénom"
            className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all"
            style={{ 
              backgroundColor: theme.card, 
              border: `1px solid ${theme.border}`,
              color: theme.text,
              boxShadow: '0 1px 4px rgba(0,0,0,0.03)'
            }}
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: theme.textLight }}>Instagram *</label>
          <input
            type="text"
            required
            value={formData.instagram}
            onChange={(e) => setFormData({...formData, instagram: e.target.value})}
            placeholder="@tonprofil"
            className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all"
            style={{ 
              backgroundColor: theme.card, 
              border: `1px solid ${theme.border}`,
              color: theme.text
            }}
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: theme.textLight }}>Email *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            placeholder="ton@email.com"
            className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all"
            style={{ 
              backgroundColor: theme.card, 
              border: `1px solid ${theme.border}`,
              color: theme.text
            }}
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: theme.textLight }}>Téléphone *</label>
          <input
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            placeholder="06 12 34 56 78"
            className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all"
            style={{ 
              backgroundColor: theme.card, 
              border: `1px solid ${theme.border}`,
              color: theme.text
            }}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: theme.textLight }}>Disponibilité *</label>
          <select
            required
            value={formData.availability}
            onChange={(e) => setFormData({...formData, availability: e.target.value})}
            className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all cursor-pointer"
            style={{ 
              backgroundColor: theme.card, 
              border: `1px solid ${theme.border}`,
              color: theme.text
            }}
          >
            <option value="">Choisir</option>
            <option>Appels journée semaine</option>
            <option>Appels soir / weekend</option>
            <option>Flexible</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: theme.textLight }}>
          {role === 'setter' ? 'Ton expérience en setting/prospection *' : 'Ton meilleur résultat en closing *'}
        </label>
        <textarea
          required
          value={formData.experience}
          onChange={(e) => setFormData({...formData, experience: e.target.value})}
          placeholder="Secteur, montant, taux de conversion, durée. Précis."
          rows={3}
          className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all resize-none"
          style={{ 
            backgroundColor: theme.card, 
            border: `1px solid ${theme.border}`,
            color: theme.text
          }}
        />
      </div>

      <div>
        <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: theme.textLight }}>
          {role === 'setter' 
            ? 'Dossier : score 24/52, CA 28k€/mois. Comment tu approches ce prospect ? *'
            : 'Dossier : food cost inconnu, score 24/52, CA 28k€/mois. Comment tu mènes l\'appel ? *'}
        </label>
        <textarea
          required
          value={formData.diagnosticApproach}
          onChange={(e) => setFormData({...formData, diagnosticApproach: e.target.value})}
          placeholder="Stratégie, ouverture, modules, gestion du prix."
          rows={4}
          className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all resize-none"
          style={{ 
            backgroundColor: theme.card, 
            border: `1px solid ${theme.border}`,
            color: theme.text
          }}
        />
      </div>

      <div>
        <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: theme.textLight }}>
          Le prospect te dit "{role === 'setter' ? 'J\'ai pas le temps' : 'C\'est trop cher'}". Mot pour mot ? *
        </label>
        <textarea
          required
          value={formData.objectionResponse}
          onChange={(e) => setFormData({...formData, objectionResponse: e.target.value})}
          placeholder="Ta réponse exacte."
          rows={3}
          className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all resize-none"
          style={{ 
            backgroundColor: theme.card, 
            border: `1px solid ${theme.border}`,
            color: theme.text
          }}
        />
      </div>

      <div>
        <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: theme.textLight }}>Pourquoi toi ? *</label>
        <textarea
          required
          value={formData.whyYou}
          onChange={(e) => setFormData({...formData, whyYou: e.target.value})}
          placeholder="Sois direct."
          rows={3}
          className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all resize-none"
          style={{ 
            backgroundColor: theme.card, 
            border: `1px solid ${theme.border}`,
            color: theme.text
          }}
        />
      </div>

      <motion.button
        type="submit"
        disabled={isSubmitting}
        whileHover={{ opacity: 0.88, y: -1 }}
        whileTap={{ scale: 0.98 }}
        className="w-full py-4 rounded-full text-sm font-extrabold tracking-wider uppercase text-white transition-all disabled:opacity-50"
        style={{ backgroundColor: theme.orange }}
      >
        {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma candidature →'}
      </motion.button>
      {submitError && (
        <motion.p 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-center py-2 px-4 rounded-lg"
          style={{ color: theme.red, backgroundColor: theme.redBg }}
        >
          {submitError}
        </motion.p>
      )}
      <p className="text-xs text-center" style={{ color: theme.textLight }}>
        Réponse sous 48h par DM Instagram · 1 seule place
      </p>
    </form>
  );
};

// ============ SETTER CONTENT ============
const SetterContent = () => {
  const [openModule, setOpenModule] = useState(null);
  const [openObjection, setOpenObjection] = useState(null);

  const processSteps = [
    { icon: '💬', title: 'Commentaire Instagram', desc: 'Le prospect commente "DIAGNOSTIC" sur une publication de Sabril', badge: 'Automatique', badgeColor: 'grey' },
    { icon: '📋', title: 'Diagnostic rempli', desc: 'Il répond à 8 questions et révèle ses vraies douleurs', badge: 'audit-resto.fr', badgeColor: 'orange' },
    { icon: '📁', title: 'Tu reçois le dossier', desc: 'Fiche complète : CA, score, piliers faibles, alertes', badge: 'Ton déclencheur', badgeColor: 'orange' },
    { icon: '⚡', title: 'TON RÔLE', desc: 'Analyse + Premier contact personnalisé → RDV 20min', badge: "C'est là que tout se joue", badgeColor: 'orange' },
    { icon: '📞', title: 'Appel de setting (20 min)', desc: 'Tu qualifies et poses le RDV stratégique', badge: '20 min max', badgeColor: 'grey' },
    { icon: '🎯', title: 'Appel closing (Sabril)', desc: "L'expert close le deal. Tu n'as pas à vendre.", badge: 'Sabril ou Closer', badgeColor: 'grey' },
    { icon: '💰', title: 'Commission versée', desc: '250€ par deal unique ou 90€ × 3 versements', badge: 'Ka-ching!', badgeColor: 'green' },
  ];

  const objections = [
    { q: '"J\'ai pas le temps"', a: '<strong style="color:#F05A1A;">Retourne l\'objection.</strong><br><br>« C\'est souvent le signe d\'un problème de structure. L\'appel c\'est 30 min — juste pour voir si on peut t\'aider. Ça t\'engage à rien, et si c\'est pas pour toi, au moins t\'auras une analyse gratuite de ta situation. »' },
    { q: '"C\'est quoi PRSNL ?"', a: '<strong style="color:#F05A1A;">Ne tout explique pas.</strong><br><br>« Un accompagnement 90 jours créé par Sabril — un restaurateur qui a vécu exactement ce que tu vis. Il te présentera ce qu\'il ferait pour TON resto lors de l\'appel. Mon rôle c\'est juste de voir si ton profil correspond. »' },
    { q: '"Combien ça coûte ?"', a: '<strong style="color:#F05A1A;">Redirige.</strong><br><br>« Sabril te donnera les détails pendant l\'appel. Ce que je sais : la plupart des restaurateurs identifient +5 000€ de pertes dans les 30 premiers jours. L\'investissement se finance lui-même. »' },
    { q: '"Je vais y réfléchir"', a: '<strong style="color:#F05A1A;">Cherche la vraie raison.</strong><br><br>« Tu réfléchis à quoi précisément ? » — puis traite l\'objection réelle.<br><br>Si timing : « Le problème que t\'as aujourd\'hui — dans 3 mois il sera exactement là. »' },
  ];

  return (
    <div className="space-y-12">
      {/* Hero */}
      <section className="text-center py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-block text-xs font-bold tracking-wider uppercase px-4 py-2 rounded-full mb-6"
          style={{ backgroundColor: theme.orangeDim, color: theme.orange, border: `1px solid ${theme.orangeBorder}` }}
        >
          ⚡ Support Setter · 1 place disponible
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="font-bebas text-7xl sm:text-8xl leading-none mb-5"
          style={{ color: theme.text }}
        >
          SETTER<br /><span style={{ color: theme.orange }}>PRSNL</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-base max-w-xl mx-auto mb-8 leading-relaxed"
          style={{ color: theme.textMuted }}
        >
          Ce document est ton <strong style={{ color: theme.text }}>support complet</strong>.
          Tu y trouves le process, les arguments, les objections, et tout ce dont tu as besoin pour qualifier les prospects.
        </motion.p>
        
        <div className="flex flex-wrap justify-center gap-2">
          {['100% Remote', '5% / deal', '250€ / close', 'Leads fournis', 'Commission sur chaque virement'].map((pill, i) => (
            <motion.div
              key={pill}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + i * 0.1 }}
            >
              <Pill>{pill}</Pill>
            </motion.div>
          ))}
        </div>
        
        {/* Link to Setter Guide */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center flex-wrap"
        >
          <Link 
            to="/setter-guide"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: theme.orange }}
            data-testid="setter-guide-link"
          >
            <FileText className="w-5 h-5" />
            Guide DM Setter
          </Link>
          <Link 
            to="/setter-call-script"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: theme.green }}
            data-testid="setter-call-script-link"
          >
            <Phone className="w-5 h-5" />
            Script Appel Setter
          </Link>
          <Link 
            to="/onboarding"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: '#7c3aed' }}
            data-testid="onboarding-link-setter"
          >
            <BookOpen className="w-5 h-5" />
            Onboarding PRSNL
          </Link>
          <Link 
            to="/crm"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-gray-700 border-2 transition-all hover:scale-105"
            style={{ borderColor: theme.border, backgroundColor: 'white' }}
            data-testid="crm-link-setter"
          >
            <BarChart3 className="w-5 h-5" />
            CRM
          </Link>
        </motion.div>
        <p className="text-xs mt-3 text-center" style={{ color: theme.textMuted }}>
          Scripts DM, appel, formation et gestion des leads
        </p>
      </section>

      {/* Section 01 - Qui tu représentes */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="01" title="Qui tu" highlight="représentes" />
        
        <ProfileCard
          initials="S"
          name="Sabril"
          tags={['Fondateur Maison Avocado', 'Ex Publicitaire', 'BFM · RMC · PSG', 'Ex Cabinet Recrutement', 'Franchise en cours']}
          bio={
            <>
              Sabril a ouvert Maison Avocado en pensant être fort en marketing et en cuisine.
              Réalité : la gestion, les chiffres, les marges — flou total. Gros CA, rien dans la poche, 65h par semaine dans le vent.<br /><br />
              Il s'est formé, accompagné, investi sur lui. 6 mois de focus sur ses vrais problèmes.
              Maison Avocado tourne. <strong style={{ color: theme.text }}>Il prépare maintenant le lancement de sa franchise — prochains points de vente en cours.</strong><br /><br />
              PRSNL c'est la méthode qu'il a construite et qu'il transmet maintenant. Pas de la théorie — de l'expérience vécue.
            </>
          }
        />

        <AlertBox type="info">
          <strong style={{ color: theme.yellow }}>À utiliser pendant l'appel :</strong> Quand le prospect doute, mentionne l'histoire de Sabril. "Il avait le même problème que toi. Gros CA, rien dans la poche. Voilà ce qu'il a fait." — c'est ton ancre de crédibilité.
        </AlertBox>
      </section>

      {/* Section 02 - Ton rôle */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="02" title="Ton rôle" highlight="de setter" />
        
        <InfoBox title="→ Tu qualifies. Tu ne vends pas." icon="🎯">
          <p>
            Le prospect a commenté "DIAGNOSTIC" et rempli le questionnaire. <strong style={{ color: theme.text }}>Il a déjà fait le premier pas.</strong>
          </p>
          <p className="mt-3">
            Ton rôle : <strong style={{ color: theme.text }}>analyser son dossier, le contacter rapidement, et poser un RDV avec Sabril ou le Closer.</strong> Tu ne closes pas — tu qualifies et tu crées la confiance.
          </p>
        </InfoBox>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 my-8">
          <TipCard icon="⏰" title="Réactivité maximale" text="Contact dans les 30 min après réception du dossier. Le prospect est chaud — ne le laisse pas refroidir." />
          <TipCard icon="📋" title="Lis le dossier avant" text="Score, piliers faibles, CA, alertes. Ne demande jamais ce qu'il t'a déjà dit. Ça brise la confiance." />
          <TipCard icon="🎯" title="Personnalise tout" text="Jamais de message générique. Ses chiffres. Son problème. 'Pour un resto comme le tien à 28k€...'" />
          <TipCard icon="🤫" title="Écoute plus que tu parles" text="Pose des questions ouvertes. Laisse-le parler de ses problèmes. Note tout pour le Closer." />
          <TipCard icon="📅" title="Objectif = RDV" text="Tu ne vends rien. Tu poses un RDV de 30min avec l'expert. C'est tout." />
          <TipCard icon="📝" title="Fiche de transmission" text="Après l'appel : note les points clés, objections, et ce qui a résonné pour le Closer." />
        </div>
      </section>

      {/* Section 03 - Le Process */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="03" title="Le process" highlight="complet" />
        
        <div className="my-8">
          {processSteps.map((step, i) => (
            <TimelineItem 
              key={i}
              icon={step.icon}
              title={step.title}
              desc={step.desc}
              badge={step.badge}
              badgeColor={step.badgeColor}
              isLast={i === processSteps.length - 1}
            />
          ))}
        </div>
      </section>

      {/* Section 04 - Objections */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="04" title="Gérer les" highlight="objections" />
        
        <div className="grid gap-3 my-6">
          {objections.map((obj, i) => (
            <ObjectionCard
              key={i}
              question={obj.q}
              answer={obj.a}
              isOpen={openObjection === i}
              onClick={() => setOpenObjection(openObjection === i ? null : i)}
            />
          ))}
        </div>
      </section>

      {/* Section 05 - Rémunération */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="05" title="Ta" highlight="rémunération" />
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
          <RemuCard label="Commission / deal" amount="5%" subtitle="du montant encaissé · versée à chaque close" highlighted />
          <RemuCard label="Refonte — 6 700€" amount="335€" subtitle="restos déjà ouverts ≥ 30k€/mois · 2j sur place" />
          <RemuCard label="Ouverture — 14 900€" amount="745€" subtitle="ouvertures complètes · 9j sur place sur 3 mois" />
          <RemuCard label="Objectif 15 appels · 4 RDV posés/mois" amount="1 000–2 000€" subtitle="potentiel 2 000–3 500€/mois en régime de croisière" highlighted />
        </div>

        <AlertBox type="info">
          <strong style={{ color: theme.yellow }}>Important :</strong> Filtre obligatoire — ne qualifie QUE des restos qui font ≥ 30 000€/mois de CA. En dessous, le lead va sur l'offre SaaS 47€ (pas de commission setter). Les commissions sont versées immédiatement après encaissement du closer.
        </AlertBox>
      </section>

      {/* Section 06 - Candidature */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="06" title="Ta" highlight="candidature" />
        <p className="text-sm mb-6" style={{ color: theme.textMuted }}>
          Pas de CV. <strong style={{ color: theme.text }}>Prouve que tu sais qualifier.</strong> Une seule place.
        </p>
        <ApplicationForm role="setter" />
      </section>
    </div>
  );
};

// ============ CLOSER CONTENT ============
const CloserContent = () => {
  const [openModule, setOpenModule] = useState(null);
  const [openObjection, setOpenObjection] = useState(null);

  const modules = [
    { num: '00', icon: '🏛️', name: 'ADN & FONDATIONS', weeks: 'Semaines 1–2', description: 'Storytelling, positionnement, concept clair, plat signature. La base sur laquelle tout le reste est construit. Un resto sans identité forte ne peut pas scaler.' },
    { num: '01', icon: '💰', name: 'FINANCES & MARGES', weeks: 'Semaines 3–4', description: 'Food cost → objectif 30%. Masse salariale → 30% du CA. Prime cost total → sous 60%. On calcule les vrais chiffres, on identifie les fuites, on construit le tableau de bord financier hebdo.' },
    { num: '02', icon: '🍽️', name: 'CARTE & CONCEPT', weeks: 'Semaines 5–6', description: 'Restructuration de la carte pour maximiser le ticket moyen et la marge par plat. Identifier les best-sellers, supprimer les pertes, créer le plat signature qui fait revenir.' },
    { num: '03', icon: '⚙️', name: 'PROCESS & DÉLÉGATION', weeks: 'Semaines 7–8', description: 'Fiches de poste, procédures opérationnelles, rituels managériaux. Le resto doit tourner sans le patron. Si 3 jours d\'absence = chaos, il n\'y a pas de système. On construit le système.' },
    { num: '04', icon: '🏆', name: 'IMAGE DE MARQUE & RÉSEAUX', weeks: 'Semaines 9–10', description: 'Stratégie Instagram, optimisation Google My Business, gestion des avis, contenu récurrent. Un resto qui n\'existe pas en ligne perd des clients chaque jour.' },
    { num: '05', icon: '📝', name: 'CONTENU & STORYTELLING', weeks: 'Semaines 10–11', description: 'L\'histoire du patron, les coulisses, les valeurs. Les gens achètent une histoire avant d\'acheter un plat. On construit la narration qui fidélise et qui attire.' },
    { num: '06', icon: '🤖', name: 'IA & AUTOMATISATION', weeks: 'Semaines 11–12', description: 'Outils IA pour automatiser les tâches répétitives : réponses aux avis, planification des publications, suivi des performances. Travailler moins. Piloter mieux.' },
  ];

  const objections = [
    { q: '"C\'est trop cher."', a: '<strong style="color:#F05A1A;">Recontextualise avec ses chiffres.</strong><br><br>« Je comprends. Mettons ça en perspective. Si ton food cost est à 38% au lieu de 30% sur [CA], tu perds [X]€ par mois. L\'accompagnement se finance lui-même en moins de 30 jours — et après ça continue à générer.<br><br>La vraie question c\'est pas "est-ce que je peux me permettre PRSNL" — c\'est "combien tu perds chaque mois en n\'ayant pas ces outils en place." »' },
    { q: '"J\'ai pas le temps."', a: '<strong style="color:#F05A1A;">Retourne l\'objection.</strong><br><br>« C\'est exactement pour ça que PRSNL existe. Si t\'as pas le temps, c\'est que ton système ne tourne pas sans toi. C\'est le premier problème qu\'on règle en module 03.<br><br>L\'engagement c\'est 1h toutes les 2 semaines — le reste c\'est du travail intégré dans ton quotidien. La question c\'est : est-ce que tu peux te permettre de continuer comme ça encore 6 mois ? »' },
    { q: '"Je vais y réfléchir."', a: '<strong style="color:#F05A1A;">Cherche la vraie objection.</strong><br><br>« Tu réfléchis à quoi précisément ? » — <em>attends sa réponse</em>.<br><br>Si prix → voir objection prix.<br>Si confiance → « Notre engagement n\'est pas un coup d\'éclat marketing. On construit une marque pilotable, durable, rentable par sa croissance — affûtée sur 7 marchés exigeants (Paris, Cannes, Marrakech, Bangtao, New York, Miami, Marbella). Si tu joues le jeu, on construit ensemble une marque qui gagne plus chaque année. »<br><br>Si timing → « Le problème que t\'as aujourd\'hui — dans 3 mois il sera exactement là. Les places sont limitées à 5. »' },
    { q: '"J\'ai déjà fait des formations."', a: '<strong style="color:#F05A1A;">Valide. Différencie.</strong><br><br>« C\'est normal — une formation c\'est du contenu générique que tu regardes seul. PRSNL c\'est différent : on travaille SUR ton resto. On calcule ton food cost ensemble. On restructure ta carte ensemble. On construit tes process ensemble. Et y\'a 6 appels live sur 90 jours, une visio de démarrage 2h avec Sabril, et les documents construits pour TON resto — pas des templates génériques. »' },
    { q: '"Je peux le faire tout seul."', a: '<strong style="color:#F05A1A;">Respecte. Questionne.</strong><br><br>« Peut-être — t\'as clairement les capacités. Mais ça fait combien de temps que tu vis cette situation ? [laisse répondre]<br><br>Ce que PRSNL apporte c\'est pas la méthode — c\'est la structure, l\'accompagnement et le regard extérieur. On voit souvent des choses qu\'un patron ne voit plus parce qu\'il est dedans tous les jours. »' },
    { q: '"Mon resto c\'est différent."', a: '<strong style="color:#F05A1A;">Valide. Montre que t\'as anticipé.</strong><br><br>« Absolument — c\'est pour ça que PRSNL n\'est pas une méthode standard. Ton profil c\'est [type de resto], [CA], avec une faiblesse sur [pilier]. Ce qu\'on construit pour toi sera différent de celui d\'un autre restaurateur.<br><br>C\'est pour ça que les places sont limitées à 5 — chaque restaurateur est vraiment accompagné, pas traité en série. »' },
    { q: '"Est-ce que les résultats sont garantis ?"', a: '<strong style="color:#F05A1A;">Pose le cadre — on ne vend pas du pute-à-clic.</strong><br><br>« Honnêtement ? On ne te promet pas X€ d\'économies en 30 jours — c\'est du marketing claqué. Ce qu\'on garantit, c\'est notre <strong>engagement long terme</strong> : on construit une marque pilotée comme un industriel — durable, duplicable, rentable par sa croissance.<br><br>Notre méthode a été affûtée sur 7 marchés où le client est ultra-exigeant (Paris, Cannes, Marrakech, Bangtao, New York, Miami, Marbella). Si tu joues le jeu (présence, exécution, KPIs hebdo), tu construis une marque qui gagne plus chaque année — pas en grattant 50€ sur les fournisseurs, mais en faisant grossir un truc qui a du sens. »' },
  ];

  const trameBlocks = [
    { time: 'Min 0 → 3 — Cadrage & rapport', phase: 'Installe la confiance. Montre que t\'as bossé.', emoji: '🤝', text: 'Remercie-le. Cadre l\'appel. Mentionne quelque chose de précis dans son diagnostic ou son compte — il doit sentir que t\'as vraiment regardé sa situation.', quote: '« [Prénom], merci d\'être là. J\'ai lu ton diagnostic avant l\'appel — t\'es sur un score de [X]/52 avec des signaux sur [pilier faible]. J\'ai aussi regardé ton Instagram et tes avis Google. On va aller directement au fond. On a 30 min — tu es prêt ? »' },
    { time: 'Min 3 → 10 — Approfondissement de la douleur', phase: 'Fais-le verbaliser son problème. Ne l\'interromps pas.', emoji: '🔍', text: 'Tu sais déjà ce qui ne va pas. Mais il doit le dire lui-même. Quand il formule sa douleur à voix haute, elle devient réelle. C\'est là que la motivation de changer se crée.', quote: '« Dans ton diagnostic tu as mentionné [douleur principale]. Ça fait combien de temps que c\'est comme ça ? »\n\n« Concrètement, quand tu regardes tes chiffres de fin de mois — qu\'est-ce que tu vois ? »\n\n« Si on règle rien dans les 6 prochains mois — t\'en es où ? »' },
    { time: 'Min 10 → 20 — La présentation ciblée', phase: 'Ses chiffres. Ses modules. Son plan.', emoji: '💡', text: 'Présente 2-3 modules maximum en les reliant directement à ses douleurs. Sois précis et chiffré. Utilise l\'histoire de Sabril comme ancre de crédibilité. Explique le déroulé concret.', quote: '« Sur ton cas précis — voilà ce qu\'on ferait. D\'abord le module Finances : on calcule ton vrai food cost, objectif 30%. Sur [son CA], ça représente environ [X]€ récupérés par mois.\n\nEnsuite le module Process : ton resto doit tourner sans toi — on construit les fiches de poste, les procédures, les rituels d\'équipe.\n\nTout ça c\'est 90 jours — avec une visio de 2h avec Sabril dès la première semaine, un appel de suivi toutes les 2 semaines, et les documents à construire module par module. C\'est pas une formation — c\'est un vrai accompagnement. »' },
    { time: 'Min 20 → 26 — Objections & investissement', phase: 'Traite les objections. Présente le prix avec confiance.', emoji: '💬', text: 'Traite les objections (voir section dédiée). Ensuite présente l\'investissement adapté au profil — Refonte pour un resto déjà ouvert qui scale, Ouverture pour un projet qui démarre.', quote: '« L\'investissement dépend de ton profil.\n\nSi t\'es déjà ouvert et tu veux scaler : Refonte — 6 700€, 2 jours sur place chez toi, 90 jours d\'accompagnement. On structure ta marque pour qu\'elle soit pilotable, scalable et rentable par sa croissance — pas par des promesses claquées.\n\nSi t\'es en cours d\'ouverture : Ouverture — 14 900€, 9 jours sur place sur 3 mois, de A à Z. Les restaurateurs qui ouvrent sans accompagnement perdent en moyenne 50 000€ la 1ère année.\n\nNotre engagement c\'est pas de te promettre X€ d\'économies en 30 jours. C\'est de construire une marque qui gagne plus chaque année, affûtée sur 7 marchés exigeants (Paris, Cannes, Marrakech, Bangtao, New York, Miami, Marbella). »' },
    { time: 'Min 26 → 30 — Le closing', phase: 'Une question. Puis silence total.', emoji: '📅', text: 'Ne ferme jamais sans poser la question directement. Après — silence. Tu n\'attends pas en parlant. Tu attends.', quote: '« Est-ce que pour toi ça a du sens de démarrer ensemble ? »\n\n— [silence] —\n\nSi oui : « Parfait. Je t\'envoie le lien Stripe dans les 5 minutes. Dès que le paiement est confirmé, Sabril te contacte pour planifier votre visio de démarrage de 2h. »\n\nSi hésitation : « Qu\'est-ce qui te retient exactement ? » → retour aux objections.' },
  ];

  return (
    <div className="space-y-12">
      {/* Hero */}
      <section className="text-center py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-block text-xs font-bold tracking-wider uppercase px-4 py-2 rounded-full mb-6"
          style={{ backgroundColor: theme.orangeDim, color: theme.orange, border: `1px solid ${theme.orangeBorder}` }}
        >
          ⚡ Support Closer · 1 place disponible
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="font-bebas text-7xl sm:text-8xl leading-none mb-5"
          style={{ color: theme.text }}
        >
          CLOSER<br /><span style={{ color: theme.orange }}>PRSNL</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-base max-w-xl mx-auto mb-8 leading-relaxed"
          style={{ color: theme.textMuted }}
        >
          Ce document est ton <strong style={{ color: theme.text }}>support complet pendant l'appel.</strong>
          Tu y trouves l'offre, les chiffres, les arguments, les objections, et la trame minute par minute.
          Reviens ici avant chaque appel.
        </motion.p>
        
        <div className="flex flex-wrap justify-center gap-2">
          {['100% Remote', '10% / deal', '500€ / close', 'Leads qualifiés', 'Commission sur chaque virement'].map((pill, i) => (
            <motion.div
              key={pill}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + i * 0.1 }}
            >
              <Pill>{pill}</Pill>
            </motion.div>
          ))}
        </div>
        
        {/* Link to Closer Call Script */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center"
        >
          <Link 
            to="/closer-call-script"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: '#7c3aed' }}
            data-testid="closer-call-script-link"
          >
            <Video className="w-5 h-5" />
            Script Visio Closer
          </Link>
          <Link 
            to="/onboarding"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: theme.green }}
            data-testid="onboarding-link"
          >
            <FileText className="w-5 h-5" />
            Onboarding PRSNL
          </Link>
          <Link 
            to="/crm"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: theme.orange }}
            data-testid="crm-link"
          >
            <BarChart3 className="w-5 h-5" />
            CRM
          </Link>
        </motion.div>
        <p className="text-xs mt-3 text-center" style={{ color: theme.textMuted }}>
          Script visio, formation et gestion des leads
        </p>
      </section>

      {/* Section 01 - Qui tu représentes */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="01" title="Qui tu" highlight="représentes" />
        
        <ProfileCard
          initials="S"
          name="Sabril"
          tags={['Fondateur Maison Avocado', 'Ex Publicitaire', 'BFM · RMC · PSG', 'Ex Cabinet Recrutement', 'Franchise en cours']}
          bio={
            <>
              Sabril a ouvert Maison Avocado en pensant être fort en marketing et en cuisine.
              Réalité : la gestion, les chiffres, les marges — flou total. Gros CA, rien dans la poche, 65h par semaine dans le vent.<br /><br />
              Il s'est formé, accompagné, investi sur lui. 6 mois de focus sur ses vrais problèmes.
              Maison Avocado tourne. <strong style={{ color: theme.text }}>Il prépare maintenant le lancement de sa franchise — prochains points de vente en cours.</strong><br /><br />
              PRSNL c'est la méthode qu'il a construite et qu'il transmet maintenant. Pas de la théorie — de l'expérience vécue.
            </>
          }
        />

        <AlertBox type="info">
          <strong style={{ color: theme.yellow }}>À utiliser pendant l'appel :</strong> Quand le prospect doute, raconte l'histoire de Sabril avec ses mots. "Il avait le même problème que toi. Gros CA, rien dans la poche. Voilà ce qu'il a fait." — c'est ton ancre de crédibilité.
        </AlertBox>
      </section>

      {/* Section 02 - Mindset */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="02" title="Le mindset" highlight="du closer PRSNL" />
        
        <InfoBox title="→ Tu diagnostiques. Tu ne vends pas." icon="🎯">
          <p>
            Le prospect a déjà commenté, rempli le diagnostic, et passé l'appel de qualification. <strong style={{ color: theme.text }}>Il sait qu'il a un problème. Il cherche la confirmation que PRSNL est fait pour lui.</strong>
          </p>
          <p className="mt-3">
            Ton rôle : analyser sa situation, lui montrer ce qu'on ferait POUR LUI, et laisser la décision lui appartenir. Un client qui signe convaincu vaut 10x un client pressé.
          </p>
        </InfoBox>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 my-8">
          <TipCard icon="📋" title="Lis le dossier avant l'appel" text="Score, piliers faibles, CA, alertes. Ne demande jamais ce qu'il t'a déjà dit. Ça brise la confiance immédiatement." />
          <TipCard icon="🎯" title="Personnalise tout" text="Jamais de pitch générique. Ses chiffres. Son food cost. Son problème. 'Pour un resto comme le tien à 28k€…'" />
          <TipCard icon="🤫" title="Les silences sont tes alliés" text="Après une question importante — tais-toi. Le premier qui parle après un silence tend à céder." />
          <TipCard icon="🧠" title="Maîtrise le vocabulaire" text="Food cost, prime cost, masse salariale, taux de remplissage. Parle son langage. Si tu tâtonnes sur les termes, tu perds sa confiance." />
          <TipCard icon="💪" title="Sois à l'aise avec le prix" text="Si toi tu trouves 6 700€ (Refonte) ou 14 900€ (Ouverture) cher, tu le transmettras. ROI < 60 jours sur un resto à 30k€/mois qui perd 5k. Internalise ça." />
          <TipCard icon="🚫" title="Ne survends jamais" text="Promettre l'impossible = chargeback + mauvaise réputation. Sois honnête sur ce que PRSNL apporte et ce qu'il n'apporte pas." />
        </div>
      </section>

      {/* Section 03 - L'offre */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="03" title="L'offre que tu" highlight="présentes" />
        
        <p className="text-sm mb-6" style={{ color: theme.textMuted }}>
          Connais cette offre sur le bout des doigts. C'est ce que tu vends. C'est ce que tu dois défendre avec conviction.
        </p>

        {/* Résultats */}
        <p className="text-xs font-bold tracking-wider uppercase mb-4" style={{ color: theme.textMuted }}>
          📊 Résultats constatés après accompagnement
        </p>
        <div className="grid grid-cols-3 gap-4 mb-8">
          <ResultCard icon="📈" value="+27%" label="CA moyen" />
          <ResultCard icon="🛒" value="+3€" label="Panier moyen" />
          <ResultCard icon="📅" value="+40%" label="Réservations" />
        </div>

        {/* Ce qui est inclus - Timeline */}
        <p className="text-xs font-bold tracking-wider uppercase mb-4" style={{ color: theme.textMuted }}>
          🗓 Ce qui est inclus — 90 jours
        </p>
        <div className="my-6">
          <TimelineItem 
            icon="🚀" 
            title="Visio de démarrage — 2h avec Sabril" 
            desc={<>Première session en tête-à-tête avec Sabril lui-même. <strong style={{ color: theme.text }}>Objectifs posés, plan d'action construit, premiers chantiers définis.</strong> On part sur les vrais chiffres du resto — food cost réel, masse salariale, CA par jour. L'accompagnement commence fort dès le premier rendez-vous.</>}
            badge="J+1 après le paiement"
            badgeColor="orange"
          />
          <TimelineItem 
            icon="📞" 
            title="Appels de suivi — 1h tous les 15 jours" 
            desc={<><strong style={{ color: theme.text }}>6 appels sur 90 jours.</strong> Chaque appel : bilan du module en cours, ajustements, questions, prochaines actions. Pas de contenu pré-enregistré — des appels live, en direct, adaptés à la situation de CE restaurateur.</>}
            badge="Tous les 15 jours"
            badgeColor="grey"
          />
          <TimelineItem 
            icon="📄" 
            title="Documents, process & fiches à établir" 
            desc={<><strong style={{ color: theme.text }}>À chaque module, le restaurateur reçoit les instructions + modèles</strong> pour construire ses propres outils : fiche de poste, tableau de bord financier, planning hebdo, process cuisine, stratégie réseaux. On ne lui donne pas des templates génériques — on construit avec lui.</>}
            badge="Module par module"
            badgeColor="orange"
          />
          <TimelineItem 
            icon="🛡️" 
            title="Engagement long terme — marque durable" 
            desc={<>On ne vend pas une promesse claquée à <strong style={{ color: theme.text }}>X€ d'économies en 30 jours</strong>. On construit une <strong style={{ color: theme.text }}>marque pilotée comme un industriel</strong> — durable, duplicable, scalable, rentable par sa <strong style={{ color: theme.text }}>croissance</strong>.<br /><br />Méthode affûtée sur 7 marchés où le client est exigeant : <strong style={{ color: theme.text }}>Paris · Cannes · Marrakech · Bangtao · New York · Miami · Marbella</strong>. On gagne plus chaque année — pas en grattant 200€ sur les fournisseurs, mais en faisant grossir une marque qui a du sens.</>}
            badge="Marque pilotable & scalable"
            badgeColor="green"
            isLast
          />
        </div>

        {/* Les 7 modules */}
        <p className="text-xs font-bold tracking-wider uppercase mb-4 mt-8" style={{ color: theme.textMuted }}>
          ⚙️ Les 7 modules
        </p>
        <div className="grid md:grid-cols-2 gap-3 my-6">
          {modules.map((mod, i) => (
            <ModuleCard
              key={i}
              {...mod}
              isOpen={openModule === i}
              onClick={() => setOpenModule(openModule === i ? null : i)}
            />
          ))}
        </div>

        {/* C'est pour qui */}
        <p className="text-xs font-bold tracking-wider uppercase mb-4 mt-8" style={{ color: theme.textMuted }}>
          ✅ C'est pour toi si… / ✗ C'est pas pour toi si…
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 my-6">
          <div className="space-y-2">
            <CheckRow type="yes">Tu fais déjà 15 000€+ de CA/mois</CheckRow>
            <CheckRow type="yes">Tu veux structurer pour scaler</CheckRow>
            <CheckRow type="yes">T'es prêt à investir sur toi</CheckRow>
            <CheckRow type="yes">Tu veux des résultats, pas du blabla</CheckRow>
          </div>
          <div className="space-y-2">
            <CheckRow type="no">Tu cherches une solution miracle</CheckRow>
            <CheckRow type="no">T'es pas prêt à changer tes habitudes</CheckRow>
            <CheckRow type="no">Tu veux juste une formation passive</CheckRow>
            <CheckRow type="no">Ton resto fait moins de 10 000€/mois</CheckRow>
          </div>
        </div>

        {/* ROI */}
        <p className="text-xs font-bold tracking-wider uppercase mb-4 mt-8" style={{ color: theme.textMuted }}>
          💸 Le ROI à présenter
        </p>
        <div 
          className="rounded-2xl p-6 my-6"
          style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, boxShadow: '0 1px 8px rgba(0,0,0,0.03)' }}
        >
          <div className="space-y-0">
            {[
              { icon: '💸', label: 'Fuites stoppées', value: '+11 440€ / mois', color: theme.green },
              { icon: '📈', label: 'Développement activé', value: '+8 300€ / mois', color: theme.green },
              { icon: '💳', label: 'Investissement PRSNL', value: '6 700€ – 14 900€', color: theme.orange },
            ].map((row, i) => (
              <div key={i} className="flex justify-between items-center py-3 border-b" style={{ borderColor: theme.border }}>
                <span className="text-sm" style={{ color: theme.textMuted }}>{row.icon} {row.label}</span>
                <span className="text-base font-extrabold" style={{ color: row.color }}>{row.value}</span>
              </div>
            ))}
          </div>
          <div className="text-center pt-6">
            <p className="text-sm mb-2" style={{ color: theme.textMuted }}>La plupart des restaurateurs identifient</p>
            <p className="font-bebas text-3xl" style={{ color: theme.orange }}>+5 000€ DE PERTES ÉVITABLES</p>
            <p className="text-sm mt-1" style={{ color: theme.textMuted }}>dans les 30 premiers jours.</p>
            <p className="text-base font-extrabold mt-4" style={{ color: theme.text }}>
              L'accompagnement se finance lui-même.<br />
              <span style={{ color: theme.textMuted, fontWeight: 400 }}>Le reste c'est du gain.</span>
            </p>
          </div>
        </div>

        {/* Pricing */}
        <p className="text-xs font-bold tracking-wider uppercase mb-4 mt-8" style={{ color: theme.textMuted }}>
          💳 Les deux offres à présenter
        </p>
        <div className="grid md:grid-cols-2 gap-4 my-6">
          <PricingCard
            featured
            label="Refonte — restos déjà ouverts"
            price="6 700€"
            subtitle="≥ 30 000€/mois CA · 2j sur place · 90 jours · Paiement Stripe sécurisé"
            commission="670€"
            commissionSub="versée immédiatement après encaissement"
          />
          <PricingCard
            label="Ouverture — projets en cours"
            price="14 900€"
            subtitle="Ouverture de A à Z · 9j sur place · 3 mois · Paiement Stripe sécurisé"
            commission="1 490€"
            commissionSub="versée immédiatement après encaissement"
          />
        </div>
        <AlertBox type="info">
          <strong style={{ color: theme.yellow }}>Filtre obligatoire :</strong> Refonte est <strong style={{ color: theme.text }}>strictement réservée aux restos ≥ 30 000€/mois</strong>. En dessous, on bascule sur l'offre SaaS 47€. Ne ferme jamais une Refonte à un resto sous ce seuil — c'est la règle Sabril.
        </AlertBox>
      </section>

      {/* Section 04 - Avant l'appel */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="04" title="Avant" highlight="l'appel" />
        
        <p className="text-sm mb-6" style={{ color: theme.textMuted }}>
          <strong style={{ color: theme.text }}>10 min de préparation = 3x plus de chances de closer.</strong> Ne saute jamais cette étape.
        </p>

        <div className="space-y-0">
          <StepItem num="01" title="Lis intégralement le diagnostic" text="Score global, piliers faibles, alertes automatiques, réponses complètes. Note les 2-3 douleurs principales. C'est sur elles que tu construis toute ta présentation." />
          <StepItem num="02" title="Calcule son potentiel de gain personnalisé" text="Sur la base de son CA déclaré, estime son food cost probable, sa masse salariale. Arrive avec DES CHIFFRES précis pour SON resto. 'Sur 28k€ de CA, un food cost à 38% au lieu de 30% = 2 240€ perdus chaque mois.'" />
          <StepItem num="03" title="Vérifie son Instagram et ses avis Google" text="Type de contenu, fréquence, engagement, note Google. Tu dois pouvoir mentionner quelque chose de précis sur son compte pendant l'appel." />
          <StepItem num="04" title="Sélectionne 2-3 modules ciblés pour lui" text="Pas besoin de tout présenter. Choisis ceux qui répondent directement à ses douleurs. Montre que c'est construit pour lui, pas générique." />
          <StepItem num="05" title="Anticipe son objection principale" text="D'après son profil — Prix ? Timing ? Confiance ? Prépare ta réponse avant qu'il la pose." />
        </div>
      </section>

      {/* Section 05 - Trame d'appel */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="05" title="La trame" highlight="minute par minute" />
        
        <p className="text-sm mb-6" style={{ color: theme.textMuted }}>
          <strong style={{ color: theme.text }}>30 min maximum.</strong> Tu mènes. Tu ne subis pas.
        </p>

        <div className="space-y-0">
          {trameBlocks.map((block, i) => (
            <TrameBlock key={i} {...block} />
          ))}
        </div>
      </section>

      {/* Section 06 - Objections */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="06" title="Gérer les" highlight="objections" />
        
        <div className="grid gap-3 my-6">
          {objections.map((obj, i) => (
            <ObjectionCard
              key={i}
              question={obj.q}
              answer={obj.a}
              isOpen={openObjection === i}
              onClick={() => setOpenObjection(openObjection === i ? null : i)}
            />
          ))}
        </div>
      </section>

      {/* Section 07 - Rémunération */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="07" title="Ta" highlight="rémunération" />
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
          <RemuCard label="Commission / deal" amount="10%" subtitle="du montant encaissé · versée à chaque close" highlighted />
          <RemuCard label="Refonte — 6 700€" amount="670€" subtitle="restos déjà ouverts ≥ 30k€/mois · 2j sur place" />
          <RemuCard label="Ouverture — 14 900€" amount="1 490€" subtitle="ouvertures complètes · 9j sur place sur 3 mois" />
          <RemuCard label="Objectif 10 appels · 3 closes/mois" amount="2 000€+" subtitle="potentiel 3 000–7 000€/mois en régime de croisière" highlighted />
        </div>

        {/* Example Box */}
        <div 
          className="rounded-2xl p-6 my-6"
          style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, boxShadow: '0 1px 6px rgba(0,0,0,0.03)' }}
        >
          <p className="text-xs font-bold tracking-wider uppercase mb-4" style={{ color: theme.textMuted }}>Exemple concret — mois type</p>
          {[
            { label: 'Appels qualifiés réalisés', value: '12' },
            { label: '2 closes Refonte', value: '2 × 670€' },
            { label: '1 close Ouverture', value: '1 × 1 490€' },
            { label: 'Bonus dépassement (>3 closes)', value: '+ 300€' },
          ].map((row, i) => (
            <div key={i} className="flex justify-between items-center py-3 border-b" style={{ borderColor: theme.border }}>
              <span className="text-sm" style={{ color: theme.textLight }}>{row.label}</span>
              <span className="text-sm font-extrabold" style={{ color: theme.text }}>{row.value}</span>
            </div>
          ))}
          <div className="flex justify-between items-center pt-4">
            <span className="text-sm font-extrabold" style={{ color: theme.text }}>Total encaissé</span>
            <span className="font-bebas text-2xl" style={{ color: theme.green }}>3 130€</span>
          </div>
        </div>

        <AlertBox type="info">
          <strong style={{ color: theme.yellow }}>Important :</strong> Les commissions sont versées <strong style={{ color: theme.text }}>immédiatement après encaissement Stripe</strong>. Plus tu closes Ouverture (14 900€), plus tu fais grimper ta moyenne — 1 490€ par deal vs 670€ sur Refonte.
        </AlertBox>
      </section>

      {/* Section 08 - Candidature */}
      <section className="border-t pt-12" style={{ borderColor: theme.border }}>
        <SectionHeader num="08" title="Ta" highlight="candidature" />
        <p className="text-sm mb-6" style={{ color: theme.textMuted }}>
          Pas de CV. <strong style={{ color: theme.text }}>Prouve que tu sais closer.</strong> Une seule place.
        </p>
        <ApplicationForm role="closer" />
      </section>
    </div>
  );
};

// ============ MAIN PAGE ============
const RecruitmentPage = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'closer' ? 'closer' : 'setter';
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [unlockedTabs, setUnlockedTabs] = useState({ setter: false, closer: false });
  const [codeInputs, setCodeInputs] = useState({ setter: '', closer: '' });
  const [codeErrors, setCodeErrors] = useState({ setter: false, closer: false });

  useEffect(() => {
    setUnlockedTabs({
      setter: localStorage.getItem('recruitment_setter') === 'true',
      closer: localStorage.getItem('recruitment_closer') === 'true'
    });
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'closer' || tab === 'setter') setActiveTab(tab);
  }, [searchParams]);

  const handleCodeSubmit = (tab) => {
    if (codeInputs[tab].toLowerCase() === ACCESS_CODES[tab]) {
      setUnlockedTabs(prev => ({ ...prev, [tab]: true }));
      localStorage.setItem(`recruitment_${tab}`, 'true');
      setCodeErrors(prev => ({ ...prev, [tab]: false }));
    } else {
      setCodeErrors(prev => ({ ...prev, [tab]: true }));
    }
  };

  const renderLockedContent = (tab) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center py-20"
    >
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8"
        style={{ backgroundColor: theme.orangeDim }}
      >
        <Lock className="w-12 h-12" style={{ color: theme.orange }} />
      </motion.div>
      
      <h2 className="font-bebas text-4xl mb-4" style={{ color: theme.text }}>CONTENU VERROUILLÉ</h2>
      <p className="text-sm mb-8 max-w-md mx-auto" style={{ color: theme.textMuted }}>
        Entre le code d'accès pour voir les détails du poste de <strong style={{ color: theme.text }}>{tab === 'setter' ? 'Setter' : 'Closer'}</strong>.
      </p>
      
      <div className="max-w-xs mx-auto">
        <input
          type="text"
          value={codeInputs[tab]}
          onChange={(e) => {
            setCodeInputs(prev => ({ ...prev, [tab]: e.target.value.toUpperCase() }));
            setCodeErrors(prev => ({ ...prev, [tab]: false }));
          }}
          onKeyDown={(e) => e.key === 'Enter' && handleCodeSubmit(tab)}
          placeholder="CODE D'ACCÈS"
          className="w-full text-xl px-5 py-4 rounded-xl text-center font-bold tracking-widest uppercase outline-none transition-colors mb-3"
          style={{ 
            backgroundColor: theme.card, 
            border: `2px solid ${codeErrors[tab] ? theme.red : theme.border}`,
            color: theme.text
          }}
        />
        {codeErrors[tab] && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm mb-3"
            style={{ color: theme.red }}
          >
            Code incorrect
          </motion.p>
        )}
        <motion.button
          whileHover={{ opacity: 0.88 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleCodeSubmit(tab)}
          className="w-full py-4 rounded-xl font-bold text-white shadow-lg"
          style={{ backgroundColor: theme.orange }}
        >
          DÉVERROUILLER
        </motion.button>
        
        {/* Code hints */}
        <p className="text-xs mt-4" style={{ color: theme.textLight }}>
          💡 Code : <span className="font-mono font-bold" style={{ color: theme.orange }}>{tab === 'setter' ? 'setter5' : 'closer10'}</span>
        </p>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.bg }} data-testid="recruitment-page">
      {/* Navigation */}
      <nav 
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-lg"
        style={{ 
          backgroundColor: 'rgba(245,243,240,0.97)', 
          borderBottom: `1px solid ${theme.border}` 
        }}
      >
        <div className="max-w-[900px] mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <a href="/" className="font-bebas text-2xl tracking-[4px]" style={{ color: theme.orange }}>PRSNL</a>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: theme.green }} />
            <span className="text-[10px] font-bold tracking-wider" style={{ color: theme.textLight }}>RECRUTEMENT</span>
          </div>
        </div>
      </nav>

      {/* Tab Switcher */}
      <div className="pt-24 px-6 sm:px-10">
        <div className="max-w-[900px] mx-auto">
          <div 
            className="flex gap-1.5 p-1.5 rounded-xl shadow-lg mb-10"
            style={{ backgroundColor: theme.card }}
          >
            {['setter', 'closer'].map((tab) => (
              <motion.button
                key={tab}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => setActiveTab(tab)}
                className="flex-1 py-4 px-6 rounded-lg font-bold text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2"
                style={{ 
                  backgroundColor: activeTab === tab ? theme.orange : 'transparent',
                  color: activeTab === tab ? '#fff' : theme.textMuted
                }}
              >
                {!unlockedTabs[tab] && <Lock className="w-4 h-4" />}
                {tab === 'setter' ? 'Setter' : 'Closer'}
                {unlockedTabs[tab] && <Check className="w-4 h-4" style={{ color: activeTab === tab ? '#fff' : theme.green }} />}
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 sm:px-10 pb-20">
        <div className="max-w-[900px] mx-auto">
          <AnimatePresence mode="wait">
            {activeTab === 'setter' && (
              <motion.div
                key="setter"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                {unlockedTabs.setter ? <SetterContent /> : renderLockedContent('setter')}
              </motion.div>
            )}
            {activeTab === 'closer' && (
              <motion.div
                key="closer"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                {unlockedTabs.closer ? <CloserContent /> : renderLockedContent('closer')}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t py-8 text-center" style={{ borderColor: theme.border }}>
        <p className="text-xs tracking-wider" style={{ color: theme.textLight }}>PRSNL © 2025 · @sabril.j · audit-resto.fr</p>
      </footer>
    </div>
  );
};

export default RecruitmentPage;
